import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { OfferSubCategory } from '../../../common/enums';
import { isProductSubcategory } from '../utils/productSubcategoryUtilsCoCo.js';
import { fetchPrereserveNumbers } from '../utils/subscriptionConfigurationApi.js';
import type { ProductOfferingsResponse } from '../../../generated/api/productOfferingsResponse.js';
import type { PlaceOrderFormData } from '../placeOrderTypes.js';

/** Maximum number of phone numbers to prereserve */
const MAX_PHONE_NUMBERS = 30;

export interface UsePhoneNumbersOptions {
  mdmId: string;
  selectedProductOffers: ProductOfferingsResponse[];
}

export interface UsePhoneNumbersResult {
  phoneNumbers: string[];
  phoneNumbersLoading: boolean;
  onLoadMoreNumbers: () => Promise<void>;
}

export const usePhoneNumbersCoCo = ({
  selectedProductOffers,
  mdmId,
}: UsePhoneNumbersOptions): UsePhoneNumbersResult => {
  const methods = useFormContext<PlaceOrderFormData>();

  const [phoneNumbers, setPhoneNumbers] = useState<string[]>([]);
  const [phoneNumbersLoading, setPhoneNumbersLoading] = useState(false);

  const lastFetchedForCount = useRef(0);

  const watchedOrderItemUnits = methods.watch('products.placeOrderItemUnits') || [];
  const selectedPhoneNumbers = watchedOrderItemUnits
    .map(unit => unit.mobileNumber)
    .filter((num): num is string => !!num);

  const voiceProductOfferingCount = selectedProductOffers.filter(
    offer =>
      isProductSubcategory(offer.productOfferingType?.subcategory, OfferSubCategory.MOBILE_VOICE) ||
      isProductSubcategory(offer.productOfferingType?.subcategory, OfferSubCategory.MOBILE_BROADBAND)
  ).length;

  useEffect(() => {
    const loadPhoneNumbers = async () => {
      if (voiceProductOfferingCount === 0) {
        return;
      }

      const targetCount = Math.min(Math.max(voiceProductOfferingCount, 6), MAX_PHONE_NUMBERS);

      if (lastFetchedForCount.current >= targetCount) {
        return;
      }

      lastFetchedForCount.current = targetCount;

      setPhoneNumbersLoading(true);
      try {
        const existingNumbers = phoneNumbers.length > 0 ? [...phoneNumbers] : [...selectedPhoneNumbers];

        const missingSelected = selectedPhoneNumbers.filter(n => !existingNumbers.includes(n));
        let combinedNumbers = [...missingSelected, ...existingNumbers];

        while (combinedNumbers.length < targetCount) {
          const remaining = targetCount - combinedNumbers.length;
          const newNumbers = await fetchPrereserveNumbers(mdmId, remaining);
          const uniqueNew = newNumbers.filter(n => !combinedNumbers.includes(n));
          if (uniqueNew.length === 0) {
            break;
          }
          combinedNumbers = [...combinedNumbers, ...uniqueNew];
        }
        setPhoneNumbers(combinedNumbers);
      } catch (error) {
        console.warn('Failed to prereserve phone numbers:', error);
      } finally {
        setPhoneNumbersLoading(false);
      }
    };

    loadPhoneNumbers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceProductOfferingCount, mdmId]);

  const onLoadMoreNumbers = useCallback(async () => {
    if (phoneNumbersLoading || phoneNumbers.length >= MAX_PHONE_NUMBERS) {
      return;
    }

    setPhoneNumbersLoading(true);
    try {
      const count = Math.min(6, MAX_PHONE_NUMBERS - phoneNumbers.length);
      const newNumbers = await fetchPrereserveNumbers(mdmId, count);
      setPhoneNumbers(prev => [...prev, ...newNumbers]);
    } catch (error) {
      console.warn('Failed to load more phone numbers:', error);
    } finally {
      setPhoneNumbersLoading(false);
    }
  }, [phoneNumbers.length, phoneNumbersLoading, mdmId]);

  return {
    phoneNumbers,
    phoneNumbersLoading,
    onLoadMoreNumbers,
  };
};
